import { useState, useEffect } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LotusIcon } from "@/components/ui/lotus-icon";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const navItems = [
  { name: "Home", href: "#hero" },
  { name: "Ons Verhaal", href: "/ons-verhaal" },
  { name: "Producten", href: "/producten" },
  { name: "Het Ritueel", href: "#ritual" },
  { name: "Reviews", href: "#reviews" },
  { name: "FAQ", href: "#faq" },
  { name: "Contact", href: "#footer" }
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setIsOpen, totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (href: string) => {
    // If it's a route (starts with /), navigate using React Router
    if (href.startsWith('/')) {
      navigate(href);
    } 
    // If it's a section anchor and we're on homepage, scroll to section
    else if (href.startsWith('#') && location.pathname === '/') {
      scrollToSection(href);
    }
    // If it's a section anchor but we're not on homepage, go to homepage first
    else if (href.startsWith('#')) {
      navigate(`/${href}`);
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href) as HTMLElement;
    if (element) {
      const offset = 100; // Account for navbar height
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/80 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            
            {/* Left - Navigation Links (Desktop) */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.slice(0, 3).map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="text-foreground hover:text-gold transition-colors duration-200 font-medium"
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* Center - Logo - CLICKABLE */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 cursor-pointer"
            >
              <LotusIcon size={32} className="text-gold-light fill-current" />
              <span className="font-serif text-xl md:text-2xl font-bold text-gold text-glow">
                LUNSSENCE
              </span>
            </button>

            {/* Right - Navigation Links (Desktop) */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.slice(3, 6).map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="text-foreground hover:text-gold transition-colors duration-200 font-medium"
                >
                  {item.name}
                </button>
              ))}
              
              {/* Cart Icon */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(true)}
                className="text-gold hover:text-gold-light hover:bg-gold/10 transition-all duration-200 hover:scale-110 relative"
              >
                <ShoppingBag className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gold text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {totalItems}
                  </span>
                )}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(true)}
                className="text-gold hover:text-gold-light hover:bg-gold/10 relative"
              >
                <ShoppingBag className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gold text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {totalItems}
                  </span>
                )}
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gold hover:text-gold-light hover:bg-gold/10"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Luxury Mobile Menu Overlay - Redesigned */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          {/* Off-white backdrop */}
          <div 
            className="absolute inset-0 bg-[#f8f6f1]" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Larger lotus watermark */}
          <div className="absolute inset-0 flex items-center justify-center opacity-8 pointer-events-none">
            <LotusIcon size={500} className="text-gold-light" />
          </div>
          
          {/* Menu content */}
          <div className="relative h-full flex flex-col justify-center items-center px-8">
            <div className="flex flex-col space-y-8 text-center mb-12">
              {navItems.map((item, index) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="font-serif text-3xl md:text-4xl font-bold text-foreground hover:text-gold transition-all duration-300 py-3 relative group"
                  style={{
                    animation: `luxury-fade-in 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  <span className="relative z-10">{item.name}</span>
                  {/* Golden underline on hover */}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-gold group-hover:w-full transition-all duration-500 ease-out" style={{ boxShadow: '0 0 10px hsl(var(--gold) / 0.6)' }} />
                </button>
              ))}
            </div>
            
            {/* Golden social media icons at bottom */}
            <div className="flex space-x-6" style={{ animation: 'luxury-fade-in 0.8s ease-out 0.7s both' }}>
              <a 
                href="https://www.instagram.com/lunssence_herbal_tea?igsh=MWptbG85aGd3eXFvcQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center hover:border-gold hover:bg-gold/10 hover:shadow-[0_0_20px_hsl(var(--gold)/0.5)] transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-gold group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@lunssence"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full border-2 border-gold flex items-center justify-center hover:border-gold hover:bg-gold/10 hover:shadow-[0_0_20px_hsl(var(--gold)/0.5)] transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-gold group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};